import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

/** Coarse node category, used only to colour the type-indicator and build the legend. */
export type OrgNodeKind =
  | 'company'
  | 'location'
  | 'team'
  | 'manager'
  | 'person'
  | 'unassigned';

/**
 * One node fed to {@link OrgGraphComponent}. Flat list with parent links — the component derives the
 * tree, lays it out, and draws the cards + connectors.
 */
export interface OrgGraphNode {
  id: string;
  parentId: string | null;
  title: string;
  subtitle?: string | null;
  /** Absolute image URL (e.g. a company logo) rendered as the node avatar. */
  imageUrl?: string | null;
  /** Fallback initials shown in a coloured circle when there is no image. */
  initials?: string | null;
  /** Avatar / accent colour (per-node, for visual variety). */
  accent?: string | null;
  /** Coarse type; drives the left-border colour + legend. A `person` with children reads as a manager. */
  kind?: OrgNodeKind | null;
  /** Small trailing badge (e.g. a child count). */
  badge?: string | number | null;
  /** Renders the card in a muted (inactive) style. */
  muted?: boolean;
}

/** Fixed type-indicator palette + legend labels. Kept 1:1 so the legend can map colour → type. */
const KIND_STYLES: Record<OrgNodeKind, { color: string; label: string }> = {
  company: { color: '#262262', label: 'Company' },
  location: { color: '#27aae1', label: 'Location' },
  team: { color: '#8e7cc3', label: 'Team' },
  manager: { color: '#e0913a', label: 'Manager' },
  person: { color: '#2bb673', label: 'Person' },
  unassigned: { color: '#94a3b8', label: 'Unassigned' },
};

const KIND_ORDER: OrgNodeKind[] = [
  'company',
  'location',
  'team',
  'manager',
  'person',
  'unassigned',
];

/** Internal node used by the Reingold–Tilford / Buchheim tidy-tree layout. */
interface LayoutNode {
  src: OrgGraphNode | null;
  kind: OrgNodeKind;
  parent: LayoutNode | null;
  children: LayoutNode[];
  childCount: number;
  collapsed: boolean;
  // RT / Buchheim working fields.
  number: number;
  prelim: number;
  mod: number;
  shift: number;
  change: number;
  thread: LayoutNode | null;
  ancestor: LayoutNode;
  // Resolved centre position (content space).
  x: number;
  y: number;
}

/** A positioned card ready for the template. `left`/`top` are the card's top-left corner. */
interface RenderNode {
  id: string;
  left: number;
  top: number;
  borderColor: string;
  typeLabel: string;
  avatarColor: string;
  initials: string;
  imageUrl: string | null;
  title: string;
  subtitle: string | null;
  muted: boolean;
  hasChildren: boolean;
  collapsed: boolean;
  childCount: number;
}

interface RenderEdge {
  id: string;
  source: string;
  target: string;
  path: string;
}

interface LegendItem {
  kind: OrgNodeKind;
  color: string;
  label: string;
}

const CARD_W = 240;
const CARD_H = 90;
const H_GAP = 34;
const V_GAP = 66;
const LEVEL_H = CARD_H + V_GAP;
const DISTANCE = CARD_W + H_GAP;
const MARGIN = 48;

const FIT_PADDING = 56;
const ZOOM_MIN = 0.2;
const ZOOM_MAX = 2.5;
const ZOOM_FACTOR = 1.2;

/**
 * Org-chart renderer. Give it a flat {@link OrgGraphNode} list (with `parentId` links); it builds the
 * tree, runs a tidy (Reingold–Tilford / Buchheim) layout — non-overlapping subtrees, parents centred
 * over their children — and draws HTML cards over an SVG connector layer inside a pan/zoom viewport.
 * Supports fit-to-view (initial + on resize + on collapse), wheel/button zoom, drag panning,
 * collapse/expand, and hover highlighting of a node's path to the root.
 */
@Component({
  selector: 'app-org-graph',
  standalone: true,
  imports: [CommonModule, NgbTooltipModule],
  templateUrl: './org-graph.component.html',
  styleUrl: './org-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgGraphComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() nodes: OrgGraphNode[] = [];

  @ViewChild('wrap', { static: true }) private readonly wrapRef!: ElementRef<HTMLElement>;

  readonly renderNodes = signal<RenderNode[]>([]);
  readonly renderEdges = signal<RenderEdge[]>([]);
  readonly contentW = signal(0);
  readonly contentH = signal(0);
  readonly legend = signal<LegendItem[]>([]);

  readonly tx = signal(0);
  readonly ty = signal(0);
  readonly scale = signal(1);
  /** Enables a CSS transform transition for programmatic (fit / button) moves only. */
  readonly smooth = signal(false);
  readonly isFullscreen = signal(false);

  readonly zoomPercent = computed(() => `${Math.round(this.scale() * 100)}%`);
  readonly viewportTransform = computed(
    () => `translate(${this.tx()}px, ${this.ty()}px) scale(${this.scale()})`
  );

  private readonly hoveredId = signal<string | null>(null);
  private readonly parentOf = new Map<string, string>();

  /** Node ids on the hovered node's path to the root (inclusive); empty when nothing is hovered. */
  readonly hoveredPath = computed<Set<string>>(() => {
    const start = this.hoveredId();
    const path = new Set<string>();
    let cursor = start;
    while (cursor) {
      path.add(cursor);
      cursor = this.parentOf.get(cursor) ?? null;
    }
    return path;
  });

  private collapsed = new Set<string>();
  private resizeObserver?: ResizeObserver;
  private smoothTimer?: ReturnType<typeof setTimeout>;
  private pointerId: number | null = null;
  private panStart = { x: 0, y: 0, tx: 0, ty: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nodes']) {
      // A fresh dataset (tab switch / show-inactive) opens fully expanded and re-fitted.
      this.collapsed = new Set();
      this.rebuild();
      this.scheduleFit();
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.fit());
    this.resizeObserver.observe(this.wrapRef.nativeElement);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    this.scheduleFit();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    if (this.smoothTimer) clearTimeout(this.smoothTimer);
  }

  toggleFullscreen(): void {
    const el = this.wrapRef.nativeElement;
    if (document.fullscreenElement === el) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void el.requestFullscreen().catch(() => undefined);
    }
  }

  private readonly onFullscreenChange = (): void => {
    this.isFullscreen.set(document.fullscreenElement === this.wrapRef.nativeElement);
    this.scheduleFit();
  };

  onCardEnter(id: string): void {
    this.hoveredId.set(id);
  }

  onCardLeave(): void {
    this.hoveredId.set(null);
  }

  isOnPath(id: string): boolean {
    return this.hoveredPath().has(id);
  }

  edgeOnPath(edge: RenderEdge): boolean {
    const path = this.hoveredPath();
    return path.has(edge.source) && path.has(edge.target);
  }

  hasHover(): boolean {
    return this.hoveredId() !== null;
  }

  toggleCollapse(id: string, event: Event): void {
    event.stopPropagation();
    if (this.collapsed.has(id)) this.collapsed.delete(id);
    else this.collapsed.add(id);
    this.collapsed = new Set(this.collapsed);
    this.rebuild();
    this.scheduleFit();
  }

  zoomIn(): void {
    this.zoomAroundCenter(this.scale() * ZOOM_FACTOR);
  }

  zoomOut(): void {
    this.zoomAroundCenter(this.scale() / ZOOM_FACTOR);
  }

  fit(): void {
    const el = this.wrapRef?.nativeElement;
    const cw = el?.clientWidth ?? 0;
    const ch = el?.clientHeight ?? 0;
    const w = this.contentW();
    const h = this.contentH();
    if (!cw || !ch || !w || !h) return;

    const k = this.clamp(
      Math.min((cw - FIT_PADDING * 2) / w, (ch - FIT_PADDING * 2) / h),
      ZOOM_MIN,
      ZOOM_MAX
    );
    this.animate(() => {
      this.scale.set(k);
      this.tx.set((cw - w * k) / 2);
      this.ty.set((ch - h * k) / 2);
    });
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const rect = this.wrapRef.nativeElement.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;
    const next = this.clamp(
      this.scale() * (event.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR),
      ZOOM_MIN,
      ZOOM_MAX
    );
    this.zoomTo(next, px, py);
  }

  onPointerDown(event: PointerEvent): void {
    // Cards and controls own their clicks; only the background pans. Capturing the pointer here for
    // a press on a button would retarget its click to the wrap and swallow it.
    if ((event.target as HTMLElement).closest('.orgg-card, button, .orgg-legend')) return;
    this.pointerId = event.pointerId;
    this.panStart = { x: event.clientX, y: event.clientY, tx: this.tx(), ty: this.ty() };
    this.wrapRef.nativeElement.setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (this.pointerId !== event.pointerId) return;
    this.tx.set(this.panStart.tx + (event.clientX - this.panStart.x));
    this.ty.set(this.panStart.ty + (event.clientY - this.panStart.y));
  }

  onPointerUp(event: PointerEvent): void {
    if (this.pointerId !== event.pointerId) return;
    this.pointerId = null;
    this.wrapRef.nativeElement.releasePointerCapture(event.pointerId);
  }

  private zoomAroundCenter(target: number): void {
    const el = this.wrapRef.nativeElement;
    this.zoomTo(this.clamp(target, ZOOM_MIN, ZOOM_MAX), el.clientWidth / 2, el.clientHeight / 2, true);
  }

  /** Zooms to `k` keeping the point (px, py) — in container coords — fixed on screen. */
  private zoomTo(k: number, px: number, py: number, smooth = false): void {
    const old = this.scale();
    if (k === old) return;
    const ratio = k / old;
    const apply = () => {
      this.tx.set(px - (px - this.tx()) * ratio);
      this.ty.set(py - (py - this.ty()) * ratio);
      this.scale.set(k);
    };
    if (smooth) this.animate(apply);
    else apply();
  }

  private animate(apply: () => void): void {
    this.smooth.set(true);
    apply();
    if (this.smoothTimer) clearTimeout(this.smoothTimer);
    this.smoothTimer = setTimeout(() => this.smooth.set(false), 360);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private scheduleFit(): void {
    requestAnimationFrame(() => this.fit());
  }

  // ---- Model → layout → render ------------------------------------------------------------------

  private rebuild(): void {
    const roots = this.buildForest();
    this.rebuildParentMap();

    const virtual = this.makeNode(null, 'person');
    virtual.children = roots;
    roots.forEach((r, i) => {
      r.parent = virtual;
      r.number = i + 1;
    });

    this.firstWalk(virtual);
    let maxDepth = 0;
    this.secondWalk(virtual, -virtual.prelim, -1, (d) => (maxDepth = Math.max(maxDepth, d)));

    const flat: LayoutNode[] = [];
    this.collect(roots, flat);

    if (!flat.length) {
      this.renderNodes.set([]);
      this.renderEdges.set([]);
      this.contentW.set(0);
      this.contentH.set(0);
      this.legend.set([]);
      return;
    }

    const minX = Math.min(...flat.map((n) => n.x));
    const maxX = Math.max(...flat.map((n) => n.x));
    const offsetX = MARGIN - (minX - CARD_W / 2);

    this.contentW.set(maxX - minX + CARD_W + MARGIN * 2);
    this.contentH.set(maxDepth * LEVEL_H + CARD_H + MARGIN * 2);

    const present = new Set<OrgNodeKind>();
    const renderNodes = flat.map((n) => {
      present.add(n.kind);
      return this.toRenderNode(n, offsetX);
    });
    this.renderNodes.set(renderNodes);
    this.renderEdges.set(this.buildEdges(flat, offsetX));
    this.legend.set(
      KIND_ORDER.filter((k) => present.has(k)).map((k) => ({
        kind: k,
        color: KIND_STYLES[k].color,
        label: KIND_STYLES[k].label,
      }))
    );
  }

  private buildForest(): LayoutNode[] {
    const byId = new Map<string, LayoutNode>();
    for (const src of this.nodes) byId.set(src.id, this.makeNode(src, this.kindOf(src)));

    const roots: LayoutNode[] = [];
    for (const src of this.nodes) {
      const node = byId.get(src.id) as LayoutNode;
      const parent = src.parentId ? byId.get(src.parentId) : undefined;
      if (parent) {
        node.parent = parent;
        parent.childCount++;
        if (!parent.collapsed) {
          node.number = parent.children.length + 1;
          parent.children.push(node);
        }
      } else {
        roots.push(node);
      }
    }
    // A person with reports reads as a manager.
    for (const node of byId.values()) {
      if (node.kind === 'person' && node.childCount > 0) node.kind = 'manager';
    }
    return roots;
  }

  private makeNode(src: OrgGraphNode | null, kind: OrgNodeKind): LayoutNode {
    const node: LayoutNode = {
      src,
      kind,
      parent: null,
      children: [],
      childCount: 0,
      collapsed: src ? this.collapsed.has(src.id) : false,
      number: 1,
      prelim: 0,
      mod: 0,
      shift: 0,
      change: 0,
      thread: null,
      ancestor: null as unknown as LayoutNode,
      x: 0,
      y: 0,
    };
    node.ancestor = node;
    return node;
  }

  private kindOf(src: OrgGraphNode): OrgNodeKind {
    return src.kind ?? 'person';
  }

  private rebuildParentMap(): void {
    this.parentOf.clear();
    for (const src of this.nodes) {
      if (src.parentId) this.parentOf.set(src.id, src.parentId);
    }
  }

  private collect(nodes: LayoutNode[], out: LayoutNode[]): void {
    for (const node of nodes) {
      out.push(node);
      this.collect(node.children, out);
    }
  }

  // ---- Buchheim (2002) tidy-tree layout ---------------------------------------------------------

  private firstWalk(v: LayoutNode): void {
    if (!v.children.length) {
      const left = this.leftSibling(v);
      v.prelim = left ? left.prelim + DISTANCE : 0;
      return;
    }
    let defaultAncestor = v.children[0];
    for (const w of v.children) {
      this.firstWalk(w);
      defaultAncestor = this.apportion(w, defaultAncestor);
    }
    this.executeShifts(v);
    const midpoint = (v.children[0].prelim + v.children[v.children.length - 1].prelim) / 2;
    const left = this.leftSibling(v);
    if (left) {
      v.prelim = left.prelim + DISTANCE;
      v.mod = v.prelim - midpoint;
    } else {
      v.prelim = midpoint;
    }
  }

  private apportion(v: LayoutNode, defaultAncestor: LayoutNode): LayoutNode {
    const w = this.leftSibling(v);
    if (!w) return defaultAncestor;

    let vir = v;
    let vor = v;
    let vil = w;
    let vol = this.leftmostSibling(v) as LayoutNode;
    let sir = v.mod;
    let sor = v.mod;
    let sil = vil.mod;
    let sol = vol.mod;

    while (this.nextRight(vil) && this.nextLeft(vir)) {
      vil = this.nextRight(vil) as LayoutNode;
      vir = this.nextLeft(vir) as LayoutNode;
      vol = this.nextLeft(vol) as LayoutNode;
      vor = this.nextRight(vor) as LayoutNode;
      vor.ancestor = v;
      const shift = vil.prelim + sil - (vir.prelim + sir) + DISTANCE;
      if (shift > 0) {
        this.moveSubtree(this.ancestorFor(vil, v, defaultAncestor), v, shift);
        sir += shift;
        sor += shift;
      }
      sil += vil.mod;
      sir += vir.mod;
      sol += vol.mod;
      sor += vor.mod;
    }
    if (this.nextRight(vil) && !this.nextRight(vor)) {
      vor.thread = this.nextRight(vil);
      vor.mod += sil - sor;
    }
    if (this.nextLeft(vir) && !this.nextLeft(vol)) {
      vol.thread = this.nextLeft(vir);
      vol.mod += sir - sol;
      defaultAncestor = v;
    }
    return defaultAncestor;
  }

  private moveSubtree(wl: LayoutNode, wr: LayoutNode, shift: number): void {
    const subtrees = wr.number - wl.number;
    wr.change -= shift / subtrees;
    wr.shift += shift;
    wl.change += shift / subtrees;
    wr.prelim += shift;
    wr.mod += shift;
  }

  private executeShifts(v: LayoutNode): void {
    let shift = 0;
    let change = 0;
    for (let i = v.children.length - 1; i >= 0; i--) {
      const w = v.children[i];
      w.prelim += shift;
      w.mod += shift;
      change += w.change;
      shift += w.shift + change;
    }
  }

  private ancestorFor(vil: LayoutNode, v: LayoutNode, defaultAncestor: LayoutNode): LayoutNode {
    return vil.ancestor.parent === v.parent ? vil.ancestor : defaultAncestor;
  }

  private nextLeft(v: LayoutNode): LayoutNode | null {
    return v.children.length ? v.children[0] : v.thread;
  }

  private nextRight(v: LayoutNode): LayoutNode | null {
    return v.children.length ? v.children[v.children.length - 1] : v.thread;
  }

  private leftSibling(v: LayoutNode): LayoutNode | null {
    if (!v.parent) return null;
    const i = v.parent.children.indexOf(v);
    return i > 0 ? v.parent.children[i - 1] : null;
  }

  private leftmostSibling(v: LayoutNode): LayoutNode | null {
    if (!v.parent) return null;
    const first = v.parent.children[0];
    return first === v ? null : first;
  }

  private secondWalk(v: LayoutNode, m: number, depth: number, track: (d: number) => void): void {
    v.x = v.prelim + m;
    v.y = depth * LEVEL_H;
    if (depth >= 0) track(depth);
    for (const w of v.children) this.secondWalk(w, m + v.mod, depth + 1, track);
  }

  // ---- Render projection ------------------------------------------------------------------------

  private toRenderNode(n: LayoutNode, offsetX: number): RenderNode {
    const src = n.src as OrgGraphNode;
    const centerX = n.x + offsetX;
    const muted = !!src.muted;
    return {
      id: src.id,
      left: centerX - CARD_W / 2,
      top: n.y + MARGIN,
      borderColor: muted ? '#cbd5e1' : KIND_STYLES[n.kind].color,
      typeLabel: KIND_STYLES[n.kind].label,
      avatarColor: muted ? '#cbd5e1' : (src.accent ?? '#27aae1'),
      initials: src.initials ?? '?',
      imageUrl: src.imageUrl ?? null,
      title: src.title,
      subtitle: src.subtitle ?? null,
      muted,
      hasChildren: n.childCount > 0,
      collapsed: n.collapsed,
      childCount: n.childCount,
    };
  }

  private buildEdges(flat: LayoutNode[], offsetX: number): RenderEdge[] {
    const edges: RenderEdge[] = [];
    for (const child of flat) {
      const parent = child.parent;
      if (!parent?.src) continue;
      const px = parent.x + offsetX;
      const py = parent.y + MARGIN + CARD_H;
      const cx = child.x + offsetX;
      const cy = child.y + MARGIN;
      const midY = (py + cy) / 2;
      edges.push({
        id: `e_${parent.src.id}_${(child.src as OrgGraphNode).id}`,
        source: parent.src.id,
        target: (child.src as OrgGraphNode).id,
        path: `M ${px} ${py} C ${px} ${midY}, ${cx} ${midY}, ${cx} ${cy}`,
      });
    }
    return edges;
  }
}
